import { useState } from "react";
import Reveal from "./Reveal";
import { cn } from "@/utils/cn";

export interface AccordionItem {
  question: string;
  answer: string;
}

interface AccordionProps {
  items: AccordionItem[];
  defaultOpen?: number | null;
  className?: string;
}

export default function Accordion({ items, defaultOpen = 0, className }: AccordionProps) {
  const [open, setOpen] = useState<number | null>(defaultOpen);

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {items.map((item, index) => {
        const isOpen = open === index;
        return (
          <Reveal
            key={item.question}
            delay={Math.min(index, 6) * 55}
            className={cn(
              "rounded-lg border bg-background-50 overflow-hidden",
              isOpen ? "border-primary-300" : "border-background-200 hover:border-background-300"
            )}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : index)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between gap-4 px-4 py-4 md:px-5 text-left cursor-pointer"
            >
              <span className="text-sm md:text-base font-label font-medium text-foreground-950">
                {item.question}
              </span>
              <span
                className={cn(
                  "flex items-center justify-center w-7 h-7 shrink-0 rounded-full transition-all duration-300",
                  isOpen ? "bg-primary-500 text-background-50 rotate-180" : "bg-background-100 text-foreground-600"
                )}
              >
                <i className={isOpen ? "ri-subtract-line" : "ri-add-line"} aria-hidden="true" />
              </span>
            </button>
            <div
              className={cn(
                "grid transition-all duration-300 ease-out",
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              )}
            >
              <div className="overflow-hidden">
                <p className="px-4 pb-4 md:px-5 text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}